import { useCallback, useEffect, useMemo, useState } from 'react';
import HotelDetails from '@customTypes/hotel-details';
import Product, { ExtendedProductAddon, ProductAddon } from '@customTypes/product';

type AddOnSelectedEvent = (item: ExtendedProductAddon, hotelDetails: HotelDetails) => void;

type UseAddOnsResult = {
  addOnItems: ExtendedProductAddon[];
  resetAddOns: () => void;
  updateAddonItem: (item: ExtendedProductAddon, value: boolean) => void;
  onAddonUnitsSelect: (value: string, item: ExtendedProductAddon) => void;
  showAddons: boolean;
};

export default function useAddOns(
  product: Product | undefined,
  hotelDetails: HotelDetails,
  addonSelected: AddOnSelectedEvent,
  adultCounter: number,
): UseAddOnsResult {
  const [addOnItems, setAddOnItems] = useState<ExtendedProductAddon[]>([]);

  const showAddons = useMemo(() => {
    if (addOnItems.length > 0 && adultCounter > 0) {
      return true;
    }

    return false;
  }, [addOnItems, adultCounter]);

  const resetAddOns = useCallback(() => {
    const addOns: ExtendedProductAddon[] = [];
    if (!product) return;

    product.addons.forEach((item: ProductAddon) => {
      if (item.active) {
        addOns.push({
          // Used for the payload:
          addon_id: item.id,
          isChecked: false,
          units: 0,

          active: item.active,
          house_description: item.house_description,
          id: item.id,
          max: item.max,
          name: item.name,
          price: item.price.toString(),
          tax_rate: item.tax_rate,
          type: item.type,
        });
      }
    });

    setAddOnItems(addOns);
  }, [product]);

  const updateAddonItem = useCallback(
    (item: ExtendedProductAddon, value: boolean) => {
      const updatedAddOns = [...addOnItems];
      const currentItem = updatedAddOns.find((addon) => addon.id === item.id);
      if (currentItem) {
        // Assign the units when the checkbox is checked, only if units is not selected first.
        currentItem.units = currentItem.units !== 0 ? currentItem.units : 1;
        currentItem.isChecked = value;
      }
      setAddOnItems(updatedAddOns);

      // Only fire the addonSelected event when the checkbox is marked.
      if (value) {
        // Event
        addonSelected(item, hotelDetails);
      }
    },
    [addOnItems, addonSelected, hotelDetails],
  );

  const onAddonUnitsSelect = useCallback(
    (value: string, item: ExtendedProductAddon) => {
      const updatedAddOns = [...addOnItems];
      const currentItem = updatedAddOns.find((addon) => addon.id === item.id);
      if (currentItem) {
        currentItem.units = +value;
      }
      setAddOnItems(updatedAddOns);
    },
    [addOnItems],
  );

  useEffect(() => {
    resetAddOns();
  }, [resetAddOns]);

  return {
    addOnItems,
    resetAddOns,
    updateAddonItem,
    onAddonUnitsSelect,
    showAddons,
  };
}
